import React from 'react';
import { Sparkles, Zap, Target, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

interface Step1LandingProps {
  onNext: () => void;
}

const Step1Landing: React.FC<Step1LandingProps> = ({ onNext }) => {
  return (
    <div className="min-h-screen bg-white overflow-hidden relative">
      {/* Remove global body padding for onboarding */}
      <style>{`
        body { padding-top: 0 !important; }
      `}</style>
      
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50"></div>
      <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-nocode-blue/10 to-nocode-purple/10 rounded-full blur-xl"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-nocode-purple/10 to-blue-400/10 rounded-full blur-xl"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="relative z-10 min-h-screen flex flex-col"
      >
        {/* Header */}
        <div className="p-6 text-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-montserrat font-bold text-2xl text-nocode-blue"
          >
            LovaBuild
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          {/* Central Graphic */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-8 relative"
          >
            <div className="w-44 h-44 mx-auto bg-gradient-to-br from-nocode-blue to-nocode-purple rounded-3xl flex items-center justify-center shadow-2xl relative overflow-hidden">
              {/* AI Brain Background */}
              <img 
                src="/lovable-uploads/c95159cb-5f23-443a-9f77-572f3a82adcd.png" 
                alt="AI Brain with Build Launch Own" 
                className="absolute inset-0 w-full h-full object-cover rounded-3xl"
              />
              
              {/* Main icon */}
              <div className="relative z-10">
                <div className="w-20 h-24 bg-white/20 rounded-xl flex flex-col items-center justify-center backdrop-blur-sm">
                  <Zap className="h-8 w-8 text-white mb-2" />
                  <div className="text-xs text-white font-semibold">AI</div>
                </div>
              </div>
              
              {/* Floating elements */}
              <motion.div
                animate={{ y: [-5, 5, -5], rotate: [0, 180, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-2 -right-2"
              >
                <Sparkles className="h-6 w-6 text-yellow-300" />
              </motion.div>
              <motion.div
                animate={{ y: [5, -5, 5], x: [-2, 2, -2] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-2 -left-2"
              >
                <Target className="h-6 w-6 text-green-300" />
              </motion.div>
            </div>
          </motion.div>

          {/* Headlines */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-montserrat font-bold mb-4 leading-tight text-gray-900 max-w-4xl"
          >
            Build, Launch, and Own Your{' '}
            <span className="bg-gradient-to-r from-nocode-blue to-nocode-purple bg-clip-text text-transparent">
              Digital Vision
            </span>{' '}
            with AI
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl leading-relaxed font-poppins"
          >
            Transform your ideas into powerful web apps, internal tools, or stunning websites without deep coding knowledge. Our AI-powered course empowers you to create and truly own your digital products.
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <Button
              onClick={onNext}
              size="lg"
              className="bg-gradient-to-r from-nocode-blue to-nocode-purple hover:from-nocode-blue/90 hover:to-nocode-purple/90 text-white font-montserrat font-semibold px-8 py-4 text-lg rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl group"
            >
              Start Building with AI
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-gray-500 font-poppins"
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>No coding required</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span>AI-powered</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <span>Own your code</span>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="p-6 text-center text-gray-400 text-sm font-poppins"
        >
          <p>© 2024 LovaBuild. All rights reserved.</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Step1Landing;